import { useMutation, useQuery, useQueryClient, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  TCreateFasterPayoutRequest,
  TFasterPayoutBank,
} from '../../../../../api/src';
import { useApi } from '../../context/ApiContextProvider';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { usePagerUrlCallbacks } from '../pager/use-pager-url-callbacks';
import { useSelectedContract } from '../contracts';
import { useUserPermissions } from '../user/users';
import {
  findNewMcFasterPayout,
  getMcFasterPayoutIdsFromCache,
  invalidateMcFasterPayoutsQueries,
  TFasterPayoutStatusResult,
} from './queries';
import { useUIDateFormat } from '../use-ui-dateFormat';

const FASTER_PAYOUT_BANKS_STALE_TIME = 60 * 60 * 1000;

export function useFasterPayoutBanksQuery(
  searchName?: string,
): UseQueryResult<TFasterPayoutBank[]> {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contractId');
  const name = searchName?.trim() || undefined;
  return useQuery<TFasterPayoutBank[]>({
    queryKey: ['faster-payouts', 'banks', contractId, name ?? ''],
    queryFn: () => api.getFasterPayoutBanks(contractId, name),
    staleTime: FASTER_PAYOUT_BANKS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export const useDropMcFasterPayoutsQueryCache = () => {
  const client = useQueryClient();
  const contractId = useURLContractId();
  const { goToPage } = usePagerUrlCallbacks();
  return useCallback(() => {
    if (!contractId) return Promise.resolve();
    return invalidateMcFasterPayoutsQueries(client, contractId).then(() =>
      goToPage(0),
    );
  }, [client, contractId, goToPage]);
};

export const useCreateFasterPayoutMutation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const contractId = useURLContractId();
  const dateFormat = useUIDateFormat();

  return useMutation({
    mutationFn: async (
      data: Omit<TCreateFasterPayoutRequest, 'contractId'>,
    ): Promise<TFasterPayoutStatusResult | null> => {
      if (!contractId) throw new Error('no contractId');
      const knownIds = getMcFasterPayoutIdsFromCache(queryClient, contractId);
      await api.createFasterPayout({ ...data, contractId });
      const created = await findNewMcFasterPayout(
        api,
        contractId,
        dateFormat,
        knownIds,
      );
      if (!created) {
        return null;
      }
      return { id: created.id, status: created.status };
    },
    onSuccess: () => {
      if (contractId) {
        invalidateMcFasterPayoutsQueries(queryClient, contractId);
        queryClient.refetchQueries({
          queryKey: ['balances', contractId],
        });
        queryClient.refetchQueries({
          queryKey: ['faster-payouts-balances', contractId],
        });
      }
    },
  });
};

/**
 * Доступность страницы создания выплаты СБП:
 * СБП на контракте + claim NEW_SBP_CREDIT у пользователя.
 */
export const useCanMakeFasterPayout = () => {
  const contract = useSelectedContract();
  const { canMakeSbpPayout } = useUserPermissions();
  return !!contract?.fasterPaymentsEnabled && canMakeSbpPayout;
};
